import { useState, useMemo, useRef, useEffect } from 'react';
import { useAuthStore } from '@/context/authStore';
import { useInventorySummary, useStockAlerts, useStockMovements, useCreateStockMovement } from '@/hooks/useInventory';
import { useProducts, useCategories, useBulkUpdateMinimumStock } from '@/hooks/useProducts';
import { useSuppliers } from '@/hooks/useSuppliers';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import AsignarCodigoModal from '@/components/AsignarCodigoModal';
import toast from 'react-hot-toast';
import { StockMovementCreate } from '@/services/inventory';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import {
  Package,
  AlertTriangle,
  Calendar,
  TrendingDown,
  Plus,
  Minus,
  RefreshCw,
  Search,
  ArrowUp,
  ArrowDown,
  Clock,
  DollarSign,
  Filter,
  Check,
  X,
  ChevronUp,
  ChevronDown,
  Scissors,
} from 'lucide-react';
import clsx from 'clsx';
import { formatPrice } from '@/utils/format';
import { matchesAllWords } from '@/utils/search';
import { Unit, formatStock, formatPricePerUnit, toBase, fromBase,
         UNIT_DIMENSION, DIMENSION_BASE_UNIT } from '@/utils/units';

// Las cantidades de movimiento SIEMPRE viajan en unidad base (gramos / unidades).
// Hay que declararlo: si no, el backend asume el display_unit del producto y un
// granel mostrado en kg termina multiplicado por 1000.
const baseUnitOf = (displayUnit: Unit): Unit => DIMENSION_BASE_UNIT[UNIT_DIMENSION[displayUnit]];

const MOVEMENT_TYPES = [
  { value: 'purchase', label: 'Compra', icon: ArrowUp, color: 'text-green-600 bg-green-100' },
  { value: 'sale', label: 'Venta', icon: ArrowDown, color: 'text-blue-600 bg-blue-100' },
  { value: 'adjustment', label: 'Ajuste', icon: RefreshCw, color: 'text-gray-600 bg-gray-100' },
  { value: 'loss', label: 'Pérdida/Merma', icon: Minus, color: 'text-red-600 bg-red-100' },
  { value: 'expired', label: 'Vencido', icon: Calendar, color: 'text-orange-600 bg-orange-100' },
  { value: 'return', label: 'Devolución', icon: ArrowUp, color: 'text-purple-600 bg-purple-100' },
  { value: 'internal_use', label: 'Uso interno', icon: Minus, color: 'text-yellow-600 bg-yellow-100' },
];

const MOVEMENT_TYPE_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  ...MOVEMENT_TYPES.map(t => ({ value: t.value, label: t.label })),
];

export default function Inventory() {
  const { user } = useAuthStore();
  // Costos / valor del inventario: solo admin/superadmin o quien pueda ver costos.
  const canViewCosts = user?.role === 'admin' || user?.role === 'superadmin'
    || (user?.effective_permissions?.includes('products_view_costs') ?? false);
  // ABM (ej. editar stock mínimo): admin/superadmin o products_write
  const canWrite = user?.role === 'admin' || user?.role === 'superadmin'
    || (user?.effective_permissions?.includes('products_write') ?? false);
  const bulkMinStock = useBulkUpdateMinimumStock();
  const [showMinStockModal, setShowMinStockModal] = useState(false);
  const [minStockValue, setMinStockValue] = useState('');
  const [activeTab, setActiveTab] = useState<'summary' | 'alerts' | 'movements'>('summary');
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Codigo escaneado que no coincidio con ningun producto
  const [codigoSinAsignar, setCodigoSinAsignar] = useState<string | null>(null);
  const [movementFilter, setMovementFilter] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showProductSearch, setShowProductSearch] = useState(false);

  // Filtros para tabla de productos en resumen
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryCategoryFilter, setInventoryCategoryFilter] = useState('');
  const [inventorySupplierFilter, setInventorySupplierFilter] = useState('');
  const [inventoryStockFilter, setInventoryStockFilter] = useState<'all' | 'low' | 'ok' | 'granel'>('all');
  // Granel desplegado: muestra que paquetes salen de el y cuantos entran con el stock actual
  const [expandedGranel, setExpandedGranel] = useState<number | null>(null);

  // Paginado de la tabla de inventario
  const INV_PAGE_SIZE = 50;
  const [invPage, setInvPage] = useState(1);

  // Estado para ordenamiento de columnas
  type SortColumn = 'name' | 'category' | 'supplier' | 'stock' | 'minimum_stock' | 'cost_price' | 'sale_price';
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Volver a la página 1 cuando cambian filtros/orden
  useEffect(() => {
    setInvPage(1);
  }, [inventorySearch, inventoryCategoryFilter, inventorySupplierFilter, inventoryStockFilter, sortColumn, sortDirection]);

  // Estado para edición inline de stock
  const [editingStockId, setEditingStockId] = useState<number | null>(null);
  const [editingStockValue, setEditingStockValue] = useState<string>('');
  const [savingStock, setSavingStock] = useState(false);
  const stockInputRef = useRef<HTMLInputElement>(null);

  // Estado del formulario de movimiento
  const [newMovement, setNewMovement] = useState<{
    product_id: number | null;
    product_name: string;
    product_current_stock: number;     // En base_unit
    product_display_unit: Unit;         // Display unit del producto seleccionado
    movement_type: string;
    quantity: string;                   // En display_unit (lo que ve el usuario)
    unit_cost: string;                  // $ por display_unit
    batch_number: string;
    expiration_date: string;
    notes: string;
  }>({
    product_id: null,
    product_name: '',
    product_current_stock: 0,
    product_display_unit: 'unit',
    movement_type: 'purchase',
    quantity: '',
    unit_cost: '',
    batch_number: '',
    expiration_date: '',
    notes: '',
  });

  const { data: summary, isLoading: loadingSummary } = useInventorySummary();
  const { data: alerts, isLoading: loadingAlerts } = useStockAlerts();
  const { data: movements, isLoading: loadingMovements } = useStockMovements({
    movement_type: movementFilter || undefined,
    limit: 100,
  });
  const { data: products, isLoading: loadingProducts } = useProducts({ only_active: true, is_combo: false });
  const { data: categories = [] } = useCategories();
  const { data: suppliers = [] } = useSuppliers();
  const createMovement = useCreateStockMovement();

  // Fraccionados agrupados por su granel padre. Los fraccionados no aparecen en esta
  // pantalla (no llevan stock propio), asi que hay que avisar en la fila del GRANEL que
  // su stock alimenta paquetes: es el unico lugar donde se puede cargar.
  const fracsByParent = useMemo(() => {
    const map = new Map<number, { id: number; name: string; grams: number }[]>();
    for (const p of products || []) {
      if (p.source_product_id && p.fraction_grams) {
        const arr = map.get(p.source_product_id) || [];
        arr.push({ id: p.id, name: p.name, grams: Number(p.fraction_grams) });
        map.set(p.source_product_id, arr);
      }
    }
    for (const arr of map.values()) arr.sort((a, b) => a.grams - b.grams);
    return map;
  }, [products]);

  // Filtrar y ordenar productos para la tabla de inventario
  const inventoryProducts = useMemo(() => {
    if (!products) return [];

    const filtered = products.filter(p => {
      // Solo productos con track_stock
      if (!p.track_stock) return false;

      // Filtro de búsqueda
      if (inventorySearch && !matchesAllWords(inventorySearch, p.name, p.code)) {
        return false;
      }

      // Filtro de categoría
      if (inventoryCategoryFilter && p.category_id !== parseInt(inventoryCategoryFilter)) {
        return false;
      }

      // Filtro de proveedor
      if (inventorySupplierFilter && p.supplier_id !== parseInt(inventorySupplierFilter)) {
        return false;
      }

      // Filtro de stock
      if (inventoryStockFilter === 'low' && Number(p.current_stock) > Number(p.minimum_stock)) {
        return false;
      }
      // "Granel": los que alimentan fraccionados. Es donde hay que cargar el stock
      // de los paquetes, asi que conviene poder aislarlos.
      if (inventoryStockFilter === 'granel' && !fracsByParent.has(p.id)) {
        return false;
      }
      if (inventoryStockFilter === 'ok' && Number(p.current_stock) <= Number(p.minimum_stock)) {
        return false;
      }

      return true;
    });

    // Ordenar productos
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'category': {
          const catA = typeof a.category === 'object' ? a.category?.name : categories.find(c => c.id === a.category_id)?.name || '';
          const catB = typeof b.category === 'object' ? b.category?.name : categories.find(c => c.id === b.category_id)?.name || '';
          comparison = (catA || '').localeCompare(catB || '');
          break;
        }
        case 'supplier': {
          const supA = a.supplier?.name || suppliers.find(s => s.id === a.supplier_id)?.name || '';
          const supB = b.supplier?.name || suppliers.find(s => s.id === b.supplier_id)?.name || '';
          comparison = supA.localeCompare(supB);
          break;
        }
        case 'stock':
          comparison = Number(a.current_stock) - Number(b.current_stock);
          break;
        case 'minimum_stock':
          comparison = Number(a.minimum_stock) - Number(b.minimum_stock);
          break;
        case 'cost_price':
          comparison = (a.cost_price || 0) - (b.cost_price || 0);
          break;
        case 'sale_price':
          comparison = (a.sale_price || 0) - (b.sale_price || 0);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [products, inventorySearch, inventoryCategoryFilter, inventorySupplierFilter, inventoryStockFilter, sortColumn, sortDirection, categories, suppliers, fracsByParent]);

  // Filtrar productos por búsqueda
  const filteredProducts = useMemo(() => {
    if (!products || !productSearch.trim()) return [];
    const search = productSearch.toLowerCase();
    return products
      .filter(p => p.track_stock && matchesAllWords(search, p.name, p.code))
      .slice(0, 10);
  }, [products, productSearch]);

  const handleSelectProduct = (product: { id: number; name: string; current_stock: number; display_unit?: string }) => {
    setNewMovement({
      ...newMovement,
      product_id: product.id,
      product_name: product.name,
      product_current_stock: product.current_stock,
      product_display_unit: ((product.display_unit as Unit) || 'unit'),
    });
    setProductSearch(product.name);
    setShowProductSearch(false);
  };

  // Pistola de código de barras: escanear un producto abre el movimiento con ese
  // producto ya elegido, listo para cargar la cantidad. Busca en TODO el catálogo,
  // sin importar los filtros de la pantalla.
  useBarcodeScanner({
    onScan: (code) => {
      const match = (products || []).find((p) => (p.code || '').trim() === code);
      if (!match) {
        // Se resuelve acá mismo: asignarle el código a un producto existente
        setCodigoSinAsignar(code);
        return;
      }
      if (!match.track_stock) {
        // Un fraccionado no tiene stock propio: lo que se carga es su granel padre.
        // Escanear el paquete abre directamente el movimiento sobre el granel.
        const padre = match.source_product_id
          ? (products || []).find((p) => p.id === match.source_product_id)
          : undefined;
        if (padre) {
          toast.success(`${match.name} se carga desde ${padre.name}`);
          handleSelectProduct(padre);
          return;
        }
        toast.error(`${match.name} no lleva control de stock`);
        return;
      }
      handleSelectProduct(match);
      setIsModalOpen(true);
      toast.success(`${match.name} — cargá la cantidad`);
    },
  });

  // Función para manejar ordenamiento de columnas
  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Componente para renderizar header con flecha de ordenamiento
  const SortableHeader = ({ column, label, align = 'left' }: { column: SortColumn; label: string; align?: 'left' | 'center' | 'right' }) => (
    <th
      className={clsx(
        'px-4 py-3 text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 transition-colors select-none',
        align === 'left' && 'text-left',
        align === 'center' && 'text-center',
        align === 'right' && 'text-right'
      )}
      onClick={() => handleSort(column)}
    >
      <div className={clsx(
        'inline-flex items-center gap-1',
        align === 'center' && 'justify-center',
        align === 'right' && 'justify-end'
      )}>
        {label}
        <span className="flex flex-col">
          <ChevronUp className={clsx(
            'h-3 w-3 -mb-1',
            sortColumn === column && sortDirection === 'asc' ? 'text-taupe-600' : 'text-gray-300'
          )} />
          <ChevronDown className={clsx(
            'h-3 w-3 -mt-1',
            sortColumn === column && sortDirection === 'desc' ? 'text-taupe-600' : 'text-gray-300'
          )} />
        </span>
      </div>
    </th>
  );

  // Funciones para edición inline de stock
  // currentStock viene en base_unit; mostramos el valor en displayUnit en el input
  const handleStartEditStock = (productId: number, currentStockBase: number, displayUnit: Unit = 'unit') => {
    setEditingStockId(productId);
    const display = fromBase(Number(currentStockBase), displayUnit);
    setEditingStockValue(display.toString());
  };

  const handleCancelEditStock = () => {
    setEditingStockId(null);
    setEditingStockValue('');
  };

  const handleSaveStock = async (productId: number, currentStockBase: number, displayUnit: Unit = 'unit') => {
    const newStockDisplay = parseFloat(editingStockValue);
    if (isNaN(newStockDisplay)) {
      handleCancelEditStock();
      return;
    }
    const newStockBase = toBase(newStockDisplay, displayUnit);
    if (newStockBase === Number(currentStockBase)) {
      handleCancelEditStock();
      return;
    }

    // El delta enviado al backend debe estar en base_unit (igual que el quantity de movimientos)
    const quantityBase = newStockBase - Number(currentStockBase);

    setSavingStock(true);
    try {
      await createMovement.mutateAsync({
        product_id: productId,
        movement_type: 'adjustment',
        quantity: quantityBase,
        quantity_unit: baseUnitOf(displayUnit),
        notes: `Ajuste manual desde inventario: ${formatStock(Number(currentStockBase), displayUnit)} → ${formatStock(newStockBase, displayUnit)}`,
      });
      handleCancelEditStock();
    } catch (error) {
      console.error('Error adjusting stock:', error);
    } finally {
      setSavingStock(false);
    }
  };

  // Focus en el input cuando se empieza a editar
  useEffect(() => {
    if (editingStockId !== null && stockInputRef.current) {
      stockInputRef.current.focus();
      stockInputRef.current.select();
    }
  }, [editingStockId]);

  const handleCreateMovement = async () => {
    if (!newMovement.product_id || !newMovement.quantity) return;

    const inputValue = parseFloat(newMovement.quantity);
    if (isNaN(inputValue)) return;
    const dispUnit = newMovement.product_display_unit;
    const inputBase = toBase(inputValue, dispUnit);
    let quantityBase: number;

    if (newMovement.movement_type === 'adjustment') {
      // Para ajustes: el usuario ingresa el NUEVO stock (en display unit), calculamos el delta en base
      quantityBase = inputBase - Number(newMovement.product_current_stock);
    } else {
      // Para otros tipos: la cantidad es directa (negativa para salidas)
      const isOutgoing = ['sale', 'loss', 'expired', 'internal_use'].includes(newMovement.movement_type);
      quantityBase = isOutgoing ? -Math.abs(inputBase) : Math.abs(inputBase);
    }

    // Convertir el unit_cost (que el usuario ingresa por display_unit) a $/base
    const unitCostBase = newMovement.unit_cost
      ? parseFloat(newMovement.unit_cost) / (dispUnit === 'kilogram' ? 1000 : 1)
      : undefined;

    const data: StockMovementCreate = {
      product_id: newMovement.product_id,
      movement_type: newMovement.movement_type as any,
      quantity: quantityBase,
      quantity_unit: baseUnitOf(dispUnit),
      unit_cost: unitCostBase,
      batch_number: newMovement.batch_number || undefined,
      expiration_date: newMovement.expiration_date || undefined,
      notes: newMovement.notes || undefined,
    };

    try {
      await createMovement.mutateAsync(data);
      setIsModalOpen(false);
      setNewMovement({
        product_id: null,
        product_name: '',
        product_current_stock: 0,
        product_display_unit: 'unit',
        movement_type: 'purchase',
        quantity: '',
        unit_cost: '',
        batch_number: '',
        expiration_date: '',
        notes: '',
      });
      setProductSearch('');
    } catch (error) {
      console.error('Error creating movement:', error);
    }
  };

  const getMovementTypeInfo = (type: string) => {
    return MOVEMENT_TYPES.find(t => t.value === type) || MOVEMENT_TYPES[2];
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'expired':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'expiring_soon':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low_stock':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getAlertLabel = (type: string) => {
    switch (type) {
      case 'expired':
        return 'Vencido';
      case 'expiring_soon':
        return 'Por vencer';
      case 'low_stock':
        return 'Stock bajo';
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventario</h1>
          <p className="text-gray-600">Gestión de stock y movimientos</p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Registrar Movimiento
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200">
        <button
          onClick={() => setActiveTab('summary')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'summary'
              ? 'border-taupe-600 text-taupe-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          Resumen
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2',
            activeTab === 'alerts'
              ? 'border-taupe-600 text-taupe-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          Alertas
          {alerts && alerts.length > 0 && (
            <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              {alerts.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('movements')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            activeTab === 'movements'
              ? 'border-taupe-600 text-taupe-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          )}
        >
          Movimientos
        </button>
      </div>

      {/* Contenido */}
      {activeTab === 'summary' && (
        <div className="space-y-6">
          {loadingSummary ? (
            <div className="text-center py-8">Cargando...</div>
          ) : summary ? (
            <>
              {/* Cards de resumen */}
              <div className={`grid grid-cols-1 md:grid-cols-2 ${canViewCosts ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Total Productos</p>
                      <p className="text-2xl font-bold text-gray-900">{summary.total_products}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Package className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Stock Bajo</p>
                      <p className="text-2xl font-bold text-yellow-600">{summary.low_stock_count}</p>
                    </div>
                    <div className="p-3 bg-yellow-100 rounded-lg">
                      <TrendingDown className="h-6 w-6 text-yellow-600" />
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Vencidos / Por Vencer</p>
                      <p className="text-2xl font-bold text-red-600">
                        {summary.expired_count} / {summary.expiring_soon_count}
                      </p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-lg">
                      <Calendar className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                </div>

                {canViewCosts && (
                  <div className="card">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm text-gray-500">Valor del Inventario</p>
                        <p
                          className="text-lg font-bold text-green-600 whitespace-nowrap"
                          title={formatPrice(summary.total_stock_value)}
                        >
                          {formatPrice(summary.total_stock_value)}
                        </p>
                      </div>
                      <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                        <DollarSign className="h-5 w-5 text-green-600" />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Acciones rápidas */}
              <div className="card">
                <h3 className="font-semibold text-gray-900 mb-4">Acciones Rápidas</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {MOVEMENT_TYPES.slice(0, 4).map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => {
                          setNewMovement({ ...newMovement, movement_type: type.value });
                          setIsModalOpen(true);
                        }}
                        className={clsx(
                          'p-4 rounded-lg border-2 border-dashed transition-all hover:border-solid',
                          'flex flex-col items-center gap-2 text-center',
                          type.color.replace('text-', 'border-').replace('-600', '-300'),
                          'hover:' + type.color.replace('-100', '-200')
                        )}
                      >
                        <Icon className={clsx('h-6 w-6', type.color.split(' ')[0])} />
                        <span className="text-sm font-medium text-gray-700">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tabla de productos del inventario */}
              <div className="card">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <h3 className="font-semibold text-gray-900">Productos en Inventario</h3>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Búsqueda */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Buscar producto..."
                        value={inventorySearch}
                        onChange={(e) => setInventorySearch(e.target.value)}
                        className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
                      />
                    </div>
                    {/* Filtro categoría */}
                    <select
                      value={inventoryCategoryFilter}
                      onChange={(e) => setInventoryCategoryFilter(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Todas las categorías</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                    {/* Filtro proveedor */}
                    <select
                      value={inventorySupplierFilter}
                      onChange={(e) => setInventorySupplierFilter(e.target.value)}
                      className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Todos los proveedores</option>
                      {suppliers.map((sup) => (
                        <option key={sup.id} value={sup.id}>{sup.name}</option>
                      ))}
                    </select>
                    {/* Filtro stock */}
                    <div className="flex gap-1">
                      <button
                        onClick={() => setInventoryStockFilter('all')}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                          inventoryStockFilter === 'all'
                            ? 'bg-primary-100 text-primary-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        )}
                      >
                        Todos
                      </button>
                      <button
                        onClick={() => setInventoryStockFilter('low')}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                          inventoryStockFilter === 'low'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        )}
                      >
                        Stock bajo
                      </button>
                      <button
                        onClick={() => setInventoryStockFilter('ok')}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                          inventoryStockFilter === 'ok'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        )}
                      >
                        Stock OK
                      </button>
                      <button
                        onClick={() => setInventoryStockFilter('granel')}
                        title="Productos a granel de los que salen paquetes fraccionados"
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-xs font-medium transition-colors inline-flex items-center gap-1',
                          inventoryStockFilter === 'granel'
                            ? 'bg-teal-100 text-teal-700'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        )}
                      >
                        <Scissors className="h-3 w-3" />
                        Granel
                      </button>
                    </div>
                    {canWrite && inventoryProducts.length > 0 && (
                      <button
                        onClick={() => { setMinStockValue(''); setShowMinStockModal(true); }}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-taupe-100 text-taupe-700 hover:bg-taupe-200 transition-colors inline-flex items-center gap-1.5"
                        title="Aplicar un stock mínimo a todos los productos del listado"
                      >
                        <RefreshCw className="h-3.5 w-3.5" />
                        Stock mínimo masivo
                      </button>
                    )}
                  </div>
                </div>

                {/* Tabla */}
                <div className="overflow-x-auto border rounded-lg">
                  {loadingProducts ? (
                    <div className="text-center py-8 text-gray-500">Cargando productos...</div>
                  ) : inventoryProducts.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                      <p>No se encontraron productos</p>
                    </div>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <SortableHeader column="name" label="Producto" align="left" />
                          <SortableHeader column="category" label="Categoría" align="left" />
                          <SortableHeader column="supplier" label="Proveedor" align="left" />
                          <SortableHeader column="stock" label="Stock" align="center" />
                          <SortableHeader column="minimum_stock" label="Mín." align="center" />
                          {canViewCosts && <SortableHeader column="cost_price" label="P. Costo" align="right" />}
                          <SortableHeader column="sale_price" label="P. Venta" align="right" />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {inventoryProducts.slice((invPage - 1) * INV_PAGE_SIZE, invPage * INV_PAGE_SIZE).map((product) => {
                          const isLowStock = Math.round(Number(product.current_stock)) < Math.round(Number(product.minimum_stock));
                          const categoryName = typeof product.category === 'object'
                            ? product.category?.name
                            : categories.find(c => c.id === product.category_id)?.name;
                          // Granel: de este producto salen paquetes fraccionados. Se pinta
                          // distinto porque es el unico lugar donde se les carga el stock.
                          const esGranel = fracsByParent.has(product.id);

                          return (
                            <tr
                              key={product.id}
                              className={clsx(esGranel ? 'bg-teal-50/60 hover:bg-teal-50' : 'hover:bg-gray-50')}
                            >
                              <td className={clsx('px-4 py-3', esGranel && 'border-l-2 border-teal-400')}>
                                <div className="flex items-center gap-2">
                                  <div className={clsx('p-1.5 rounded-lg', esGranel ? 'bg-teal-100' : 'bg-blue-100')}>
                                    {esGranel
                                      ? <Scissors className="h-4 w-4 text-teal-600" />
                                      : <Package className="h-4 w-4 text-blue-600" />}
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">{product.name}</p>
                                    {product.code && (
                                      <p className="text-xs text-gray-500 font-mono">{product.code}</p>
                                    )}
                                    {esGranel && (() => {
                                      const fracs = fracsByParent.get(product.id)!;
                                      const abierto = expandedGranel === product.id;
                                      const stockG = Number(product.current_stock);
                                      return (
                                        <>
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setExpandedGranel(abierto ? null : product.id);
                                            }}
                                            className="text-[11px] text-teal-700 hover:text-teal-900 flex items-center gap-1 mt-0.5"
                                            title="Ver que paquetes salen de este granel"
                                          >
                                            <Scissors className="h-3 w-3 flex-shrink-0" />
                                            Alimenta {fracs.length} fraccionado{fracs.length > 1 ? 's' : ''}
                                            {abierto
                                              ? <ChevronUp className="h-3 w-3" />
                                              : <ChevronDown className="h-3 w-3" />}
                                          </button>
                                          {abierto && (
                                            <ul className="mt-1 ml-1 pl-2 border-l-2 border-teal-200 space-y-0.5">
                                              {fracs.map((f) => {
                                                // Paquetes que entran con el stock que hay hoy
                                                const posibles = f.grams > 0 ? Math.floor(stockG / f.grams) : 0;
                                                return (
                                                  <li key={f.id} className="text-[11px] text-gray-600">
                                                    <span className="font-medium text-gray-800">{f.name}</span>
                                                    <span className="text-gray-400"> · {f.grams} g</span>
                                                    <span className={posibles > 0 ? 'text-teal-700' : 'text-red-600'}>
                                                      {' '}· {posibles > 0 ? `alcanza para ${posibles} u.` : 'sin stock'}
                                                    </span>
                                                  </li>
                                                );
                                              })}
                                            </ul>
                                          )}
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {categoryName || '-'}
                              </td>
                              <td className="px-4 py-3 text-gray-600">
                                {product.supplier?.name || suppliers.find(s => s.id === product.supplier_id)?.name || '-'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {(() => {
                                  const dispUnit: Unit = (product.display_unit as Unit) || 'unit';
                                  const isWeight = product.dimension === 'weight';
                                  return editingStockId === product.id ? (
                                    <div className="inline-flex items-center gap-1">
                                      <input
                                        ref={stockInputRef}
                                        type="number"
                                        step={isWeight ? 'any' : '1'}
                                        min="0"
                                        value={editingStockValue}
                                        onChange={(e) => setEditingStockValue(isWeight ? e.target.value : e.target.value.replace(/[^0-9]/g, ''))}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter') {
                                            handleSaveStock(product.id, product.current_stock, dispUnit);
                                          } else if (e.key === 'Escape') {
                                            handleCancelEditStock();
                                          }
                                        }}
                                        className="w-20 px-2 py-1 text-center text-sm font-bold border-2 border-blue-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
                                        disabled={savingStock}
                                      />
                                      <button
                                        onClick={() => handleSaveStock(product.id, product.current_stock, dispUnit)}
                                        disabled={savingStock}
                                        className="p-1 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors disabled:opacity-50"
                                        title="Confirmar"
                                      >
                                        <Check className="h-4 w-4" />
                                      </button>
                                      <button
                                        onClick={handleCancelEditStock}
                                        disabled={savingStock}
                                        className="p-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors disabled:opacity-50"
                                        title="Cancelar"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => handleStartEditStock(product.id, product.current_stock, dispUnit)}
                                      className={clsx(
                                        'inline-flex items-center px-2.5 py-1 rounded-full text-sm font-bold cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all',
                                        Number(product.current_stock) < 0
                                          ? 'bg-purple-100 text-purple-700 ring-2 ring-purple-300'
                                          : isLowStock
                                            ? Number(product.current_stock) === 0
                                              ? 'bg-red-100 text-red-700'
                                              : 'bg-orange-100 text-orange-700'
                                            : 'bg-green-100 text-green-700'
                                      )}
                                      title="Clic para ajustar stock"
                                    >
                                      {formatStock(Number(product.current_stock), dispUnit)}
                                      {product.current_stock < 0 && ' ⚠️'}
                                    </button>
                                  );
                                })()}
                              </td>
                              <td className="px-4 py-3 text-center text-gray-500">
                                {formatStock(Number(product.minimum_stock), (product.display_unit as Unit) || 'unit')}
                              </td>
                              {canViewCosts && (
                                <td className="px-4 py-3 text-right text-gray-600">
                                  {product.cost_price != null ? formatPricePerUnit(product.cost_price, (product.display_unit as Unit) || 'unit') : '-'}
                                </td>
                              )}
                              <td className="px-4 py-3 text-right font-medium text-green-600">
                                {product.sale_price != null ? formatPricePerUnit(product.sale_price, (product.display_unit as Unit) || 'unit') : '-'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
                {inventoryProducts.length > INV_PAGE_SIZE && (
                  <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
                    <p className="text-sm text-gray-500">
                      {(invPage - 1) * INV_PAGE_SIZE + 1}–{Math.min(invPage * INV_PAGE_SIZE, inventoryProducts.length)} de {inventoryProducts.length} productos
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setInvPage((p) => Math.max(1, p - 1))}
                        disabled={invPage <= 1}
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      <span className="text-sm text-gray-600 px-2">
                        Pág. {invPage} de {Math.ceil(inventoryProducts.length / INV_PAGE_SIZE)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setInvPage((p) => Math.min(Math.ceil(inventoryProducts.length / INV_PAGE_SIZE), p + 1))}
                        disabled={invPage >= Math.ceil(inventoryProducts.length / INV_PAGE_SIZE)}
                        className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="card">
          {loadingAlerts ? (
            <div className="text-center py-8">Cargando...</div>
          ) : alerts && alerts.length > 0 ? (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div
                  key={`${alert.id}-${alert.alert_type}`}
                  className={clsx(
                    'p-4 rounded-lg border flex items-center justify-between',
                    getAlertColor(alert.alert_type)
                  )}
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-5 w-5" />
                    <div>
                      <p className="font-medium">{alert.name}</p>
                      {alert.code && <p className="text-sm opacity-75">{alert.code}</p>}
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/50">
                      {getAlertLabel(alert.alert_type)}
                    </span>
                    <div className="mt-1 text-sm">
                      {alert.alert_type === 'low_stock' && (
                        <span>Stock: {alert.current_stock} / Mín: {alert.minimum_stock}</span>
                      )}
                      {(alert.alert_type === 'expired' || alert.alert_type === 'expiring_soon') && (
                        <span>
                          {alert.days_until_expiration !== undefined && alert.days_until_expiration < 0
                            ? `Venció hace ${Math.abs(alert.days_until_expiration)} días`
                            : `Vence en ${alert.days_until_expiration} días`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <AlertTriangle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              No hay alertas de inventario
            </div>
          )}
        </div>
      )}

      {activeTab === 'movements' && (
        <div className="space-y-4">
          {/* Filtros */}
          <div className="card">
            <div className="flex items-center gap-4">
              <Filter className="h-4 w-4 text-gray-400" />
              <Select
                options={MOVEMENT_TYPE_OPTIONS}
                value={movementFilter}
                onChange={(e) => setMovementFilter(e.target.value)}
                className="w-48"
              />
            </div>
          </div>

          {/* Lista de movimientos */}
          <div className="card overflow-hidden">
            {loadingMovements ? (
              <div className="text-center py-8">Cargando...</div>
            ) : movements && movements.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Fecha
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Tipo
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Producto
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Cantidad
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Stock
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                        Notas
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {movements.map((movement) => {
                      const typeInfo = getMovementTypeInfo(movement.movement_type);
                      const Icon = typeInfo.icon;
                      return (
                        <tr key={movement.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              {formatDate(movement.created_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={clsx(
                              'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium',
                              typeInfo.color
                            )}>
                              <Icon className="h-3 w-3" />
                              {typeInfo.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {movement.product_name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={clsx(
                              'text-sm font-bold',
                              movement.quantity > 0 ? 'text-green-600' : 'text-red-600'
                            )}>
                              {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {movement.previous_stock} → {movement.new_stock}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {movement.notes || '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No hay movimientos registrados
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de nuevo movimiento */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setProductSearch('');
        }}
        title="Registrar Movimiento de Stock"
        size="lg"
      >
        <div className="space-y-4">
          {/* Tipo de movimiento */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Movimiento
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {MOVEMENT_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => setNewMovement({ ...newMovement, movement_type: type.value })}
                    className={clsx(
                      'p-2 rounded-lg border text-sm font-medium transition-colors flex items-center justify-center gap-1',
                      newMovement.movement_type === type.value
                        ? type.color + ' border-current'
                        : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {type.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Búsqueda de producto */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Producto
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  setShowProductSearch(true);
                  if (!e.target.value) {
                    setNewMovement({ ...newMovement, product_id: null, product_name: '' });
                  }
                }}
                onFocus={() => setShowProductSearch(true)}
                placeholder="Buscar producto..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
              />
            </div>

            {showProductSearch && filteredProducts.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto">
                {filteredProducts.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => handleSelectProduct(product)}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-gray-400" />
                      <span className="text-sm">{product.name}</span>
                      {product.code && (
                        <span className="text-xs text-gray-400">({product.code})</span>
                      )}
                    </div>
                    <span className="text-sm text-gray-500">
                      Stock: {formatStock(Number(product.current_stock), (product.display_unit as Unit) || 'unit')}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Cantidad */}
          {(() => {
            const dispUnit = newMovement.product_display_unit;
            const isWeight = dispUnit !== 'unit';
            const stepVal = isWeight ? '0.01' : '1';
            const unitLabel = dispUnit === 'kilogram' ? 'kg' : dispUnit === 'gram' ? 'g' : 'unidades';

            if (newMovement.movement_type === 'adjustment') {
              const inputDisplay = parseFloat(newMovement.quantity);
              const currentStockDisplay = fromBase(Number(newMovement.product_current_stock), dispUnit);
              const diff = !isNaN(inputDisplay) ? inputDisplay - currentStockDisplay : 0;
              return (
                <div className="space-y-3">
                  {newMovement.product_id && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-blue-700">Stock actual:</span>
                        <span className="text-lg font-bold text-blue-800">{formatStock(Number(newMovement.product_current_stock), dispUnit)}</span>
                      </div>
                    </div>
                  )}
                  <Input
                    type="number"
                    step={stepVal}
                    min="0"
                    label={`Nuevo Stock (${unitLabel})`}
                    value={newMovement.quantity}
                    onChange={(e) => setNewMovement({ ...newMovement, quantity: e.target.value })}
                    placeholder={`Nuevo valor en ${unitLabel}`}
                  />
                  {newMovement.product_id && newMovement.quantity && !isNaN(inputDisplay) && (
                    <div className={clsx(
                      'p-3 rounded-lg border',
                      diff > 0
                        ? 'bg-green-50 border-green-200'
                        : diff < 0
                        ? 'bg-red-50 border-red-200'
                        : 'bg-gray-50 border-gray-200'
                    )}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">Diferencia:</span>
                        <span className={clsx(
                          'text-lg font-bold',
                          diff > 0
                            ? 'text-green-600'
                            : diff < 0
                            ? 'text-red-600'
                            : 'text-gray-600'
                        )}>
                          {diff > 0 ? '+' : ''}
                          {diff} {unitLabel}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  step={stepVal}
                  min="0"
                  label={`Cantidad (${unitLabel})`}
                  value={newMovement.quantity}
                  onChange={(e) => setNewMovement({ ...newMovement, quantity: e.target.value })}
                  placeholder="Cantidad"
                />
                {newMovement.movement_type === 'purchase' && (
                  <Input
                    type="number"
                    step="0.01"
                    label={`Costo unit./${dispUnit === 'kilogram' ? 'kg' : dispUnit === 'gram' ? 'g' : 'u'}`}
                    value={newMovement.unit_cost}
                    onChange={(e) => setNewMovement({ ...newMovement, unit_cost: e.target.value })}
                    placeholder="$0.00"
                  />
                )}
              </div>
            );
          })()}

          {/* Lote y vencimiento */}
          {(newMovement.movement_type === 'purchase' || newMovement.movement_type === 'return') && (
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Número de Lote"
                value={newMovement.batch_number}
                onChange={(e) => setNewMovement({ ...newMovement, batch_number: e.target.value })}
                placeholder="Lote"
              />
              <Input
                type="date"
                label="Fecha de Vencimiento"
                value={newMovement.expiration_date}
                onChange={(e) => setNewMovement({ ...newMovement, expiration_date: e.target.value })}
              />
            </div>
          )}

          {/* Notas */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notas
            </label>
            <textarea
              value={newMovement.notes}
              onChange={(e) => setNewMovement({ ...newMovement, notes: e.target.value })}
              rows={2}
              className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 outline-none"
              placeholder="Observaciones..."
            />
          </div>

          {/* Botones */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setProductSearch('');
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleCreateMovement}
              loading={createMovement.isPending}
              disabled={!newMovement.product_id || !newMovement.quantity}
            >
              Registrar Movimiento
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal: stock mínimo masivo (aplica a todo el listado filtrado) */}
      <Modal
        isOpen={showMinStockModal}
        onClose={() => setShowMinStockModal(false)}
        title="Stock mínimo masivo"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Se aplicará el mismo stock mínimo a los <strong>{inventoryProducts.length}</strong> productos
            del listado actual (respeta el filtro/búsqueda). El valor se interpreta en la unidad de cada
            producto (u / kg / g).
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Stock mínimo</label>
            <input
              type="number"
              min="0"
              step="any"
              value={minStockValue}
              onChange={(e) => setMinStockValue(e.target.value)}
              placeholder="Ej: 10"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setShowMinStockModal(false)}>Cancelar</Button>
            <Button
              loading={bulkMinStock.isPending}
              disabled={minStockValue === '' || Number(minStockValue) < 0}
              onClick={async () => {
                await bulkMinStock.mutateAsync({
                  product_ids: inventoryProducts.map((p) => p.id),
                  minimum_stock: Number(minStockValue),
                });
                setShowMinStockModal(false);
              }}
            >
              Aplicar a {inventoryProducts.length}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Escaneaste un codigo que no esta en ningun producto: asignarselo a uno */}
      <AsignarCodigoModal
        code={codigoSinAsignar}
        products={products || []}
        onClose={() => setCodigoSinAsignar(null)}
      />
    </div>
  );
}
